import CommonApi from '@/pages/api'
import { Box } from '@mui/material'
import { useEffect, useState, useCallback } from 'react'
import MarketData from './components/MarketData'
import { LOOK_UP_DUMMY_DATA } from './utils'
import { CurrentStats } from '@/pages/api/endpoints/MarketData'

import { useStore } from '@/src/types/store'
import WelcomeSection from './components/WelcomeSection'
import Announcements from './components/AnnouncementSection'
import BoardSection from './components/BoardSection'
import MediaSection from './components/MediaSection'
import NewsSection from './components/NewsSection'
import VideosSection from './components/VideosSection'
import QuestionSection from './components/QuestionSection'
import InformedSection from './components/InformedSection'

interface HomeSectionPrefItem {
  id: number
  section_id: number
  title?: string
  description?: string
  is_editable?: boolean
  show_section?: boolean
  show_news_section?: boolean
  show_news_menu?: boolean
}

const NEWS_SECTION_ID = 5

const Dashboard = ({ isDraft = false }) => {
  const { setStatsData } = useStore()
  const [sections, setSections] = useState([])
  const [loading, setLoading] = useState(false)
  const [marketData, setMarketData] = useState<any>()

  const currentStats = useCallback(
    async (value?: any) => {
      try {
        const resp = await CommonApi({
          path: CurrentStats?.get(
            value ?? {
              interval: 'three_month',
              start: '2021-04-05T05:29:40.530Z',
              end: '2023-04-05T05:29:40.530Z'
            }
          ),
          method: 'get'
        })
        if (resp.status) {
          setStatsData(resp.data)
        }
      } catch (err) {
        console.log('err', err)
      }
    },
    [setStatsData]
  )

  const getMarketDataDetails = useCallback(async () => {
    try {
      setLoading(true)
      const resp = await CommonApi({
        path: 'market-data',
        method: 'get'
      })

      if (resp.status) {
        setMarketData(resp?.data)
      }
      setLoading(false)
    } catch (err) {
      console.log('err', err)
    }
  }, [])

  const fetchHomepageSections = useCallback(async () => {
    try {
      const response = await CommonApi({
        path: `show-homepage-sections${isDraft ? '?status=draft' : ''}`,
        method: 'get'
      })

      if (response?.status && Array.isArray(response.data)) {
        setSections(response?.data)
      }
    } catch (error) {
      console.error('Failed to fetch home prefs:', error)
    }
  }, [isDraft])

  useEffect(() => {
    getMarketDataDetails()
  }, [getMarketDataDetails])

  // fetch data every 10 minutes
  useEffect(() => {
    const interval = setInterval(
      () => {
        currentStats()
      },
      10 * 60 * 1000
    )
    return () => clearInterval(interval)
  }, [currentStats])

  useEffect(() => {
    fetchHomepageSections()
  }, [fetchHomepageSections])

  const renderSection = (item: HomeSectionPrefItem) => {
    switch (item?.section_id) {
      case 2:
        return <WelcomeSection sectionId={item?.section_id} isDraft={isDraft} />
      case 3:
        return <Announcements sectionId={item?.section_id} isDraft={isDraft} />
      case 4:
        return <BoardSection sectionId={item?.section_id} isDraft={isDraft} />
      case 5:
        return (
          <NewsSection
            sectionId={item?.section_id}
            isDraft={isDraft}
            showNewsMenu={item?.show_news_menu !== false}
          />
        )
      case 6:
        return <MediaSection sectionId={item?.section_id} isDraft={isDraft} />
      case 7:
        return (
          <MarketData
            sectionId={item?.section_id}
            marketData={LOOK_UP_DUMMY_DATA(marketData, true)}
            loading={loading}
            newGraph={true}
            newMarketData={marketData}
          />
        )
      case 8:
        return <QuestionSection sectionId={item?.section_id} isDraft={isDraft} />
      case 9:
        return <InformedSection sectionId={item?.section_id} isDraft={isDraft} />
      case 10:
        return <VideosSection sectionId={item?.section_id} isDraft={isDraft} />
      default:
        return null
    }
  }

  return (
    <Box className="w-full">
      {sections?.map((item: any, index: number) => {
        const isLastItem = index === sections.length - 1
        const isSection9 = item.section_id === 9
        const isVisible =
          item?.section_id === NEWS_SECTION_ID
            ? item?.show_section && item?.show_news_section !== false
            : item?.show_section
        if (isVisible) {
          return (
            <div
              key={item?.section_id ?? index}
              className={`${
                isLastItem && isSection9
                  ? ''
                  : 'xl:max-w-[1650px] xl:mx-auto px-[1rem] md:px-[4.25rem] lg:px-[10.25rem] py-[1rem] md:py-[3.375rem] flex flex-col'
              } `}
            >
              {renderSection(item)}
            </div>
          )
        }
      })}
    </Box>
  )
}

export default Dashboard
